import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  MAT_DATE_FORMATS,
  DateAdapter,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  LuxonDateAdapter,
  MatLuxonDateModule,
} from '@angular/material-luxon-adapter';
import { MatTabsModule } from '@angular/material/tabs';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CUSTOM_DATE_FORMATS } from './shared/extensions/custom-date-format';
import {
  DashboardPage,
  DictionaryACTypesTab,
  DictionarySapSymbolsTab,
  DictionaryFuelTypesTab,
  DictionaryCurrenciesTab,
  DictionaryUnitConverterTab,
  DictionaryParametersTab,
  DictionariesPapIndexTab,
  DictionaryMessageTypesFuelAppTab,
} from '../components/pages';
import { DictionaryLayout } from '../components/shared';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from './core/header/header.component';
import { SidenavComponent } from './core/sidenav/sidenav.component';
import { AddFuelTypeDialogComponent } from '../components/pages/dictionaries-fueltypes-tab/add-fuel-type-dialog/add-fuel-type-dialog.component';
import { AddCurrencyDialogComponent } from '../components/pages/dictionaries-currencies-tab/add-currency-dialog/add-currency-dialog.component';
import { AddUnitConverterDialogComponent } from '../components/pages/dictionaries-unit-converter-tab/add-unit-converter-dialog/add-unit-converter-dialog.component';
import { ConfirmationDialogComponent } from '../components/shared/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from '../components/shared/error-dialog/error-dialog.component';
import { DataImportErrorDialog } from '../components/shared/data-import-error-dialog/data-import-error-dialog';
import { DictionariesParametersAddNewDialogComponent } from '../components/pages/dictionaries-parameters-tab/dictionaries-parameters-add-new-dialog/dictionaries-parameters-add-new-dialog.component';
import { AddNewAcTypeDialogComponent } from '../components/pages/dictionaries-actypes-tab/add-new-ac-type-dialog/add-new-ac-type-dialog.component';
import { AddNewSapSymbolsDialogComponent } from '../components/pages/dictionaries-sap-symbols-tab/add-new-sap-symbols-dialog/add-new-sap-symbols-dialog.component';
import { AddNewPapIndexDialogComponent } from '../components/pages/dictionaries-pap-index-tab/add-new-pap-index-dialog/add-new-pap-index-dialog.component';
import { FuelSupplierDialogComponent } from './fuel-contracts/fuel-suppliers/fuel-supplier-dialog/fuel-supplier-dialog.component';
import { SharedModule } from './shared/shared.module';
import { MatBadgeModule } from '@angular/material/badge';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from './auth';
import { UnauthorizedInterceptor } from './shared/interceptors/unauthorized.interceptor';
import { TimeoutInterceptor } from './shared/interceptors/timeout.interceptor';
import { DictionariesFuelFeesTab } from 'src/components/pages/dictionaries-fuelfees-tab/dictionaries-fuelfees-tab';
import { DictionariesFuelPriceIndexTab } from 'src/components/pages/dictionaries-fuel-price-index-tab/dictionaries-fuel-price-index-tab';
import { DictionariesFuelPriceIndexAddNewDialogComponent } from 'src/components/pages/dictionaries-fuel-price-index-tab/dictionaries-fuel-price-index-add-new-dialog/dictionaries-fuel-price-index-add-new-dialog.component';
import { DictionariesFuelFeesAddNewDialogComponent } from 'src/components/pages/dictionaries-fuelfees-tab/dictionaries-fuelfees-add-new-dialog/dictionaries-fuelfees-add-new-dialog.component';
import { ContractDetailsDialogComponent } from './fuel-contracts/airport-contract-list/contract-details-dialog/contract-details-dialog.component';
import { FuelSupplierAttachmentsDialogComponent } from './fuel-contracts/fuel-suppliers/fuel-supplier-attachments-dialog/fuel-supplier-attachments-dialog.component';
import { SmartNumberPipe } from './shared/pipes/smart-number.pipe';
import { AddMessageTypeDialogComponent } from '../components/pages/dictionaries-message-types-fuel-app-tab/add-message-type-dialog/add-message-type-dialog.component';

@NgModule({
  declarations: [
    AppComponent,

    // pages components
    DashboardPage,
    DictionaryACTypesTab,
    DictionarySapSymbolsTab,
    DictionaryUnitConverterTab,
    DictionaryFuelTypesTab,
    DictionaryCurrenciesTab,
    DictionaryParametersTab,
    DictionariesFuelFeesTab,
    DictionariesFuelPriceIndexTab,
    DictionariesPapIndexTab,
    DictionaryMessageTypesFuelAppTab,
    AddUnitConverterDialogComponent,
    AddFuelTypeDialogComponent,
    AddCurrencyDialogComponent,
    AddMessageTypeDialogComponent,
    DictionariesParametersAddNewDialogComponent,
    DictionariesFuelFeesAddNewDialogComponent,
    DictionariesFuelPriceIndexAddNewDialogComponent,
    ContractDetailsDialogComponent,
    SmartNumberPipe,

    // shared components
    HeaderComponent,
    SidenavComponent,
    DictionaryLayout,
    ConfirmationDialogComponent,
    ErrorDialogComponent,
    AddNewAcTypeDialogComponent,
    AddNewSapSymbolsDialogComponent,
    AddNewPapIndexDialogComponent,
    FuelSupplierDialogComponent,
    FuelSupplierAttachmentsDialogComponent,
    DataImportErrorDialog,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatInputModule,
    MatDatepickerModule,
    MatLuxonDateModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    HttpClientModule,
    ReactiveFormsModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatMenuModule,
    MatSidenavModule,
    MatCardModule,
    MatTabsModule,
    FormsModule,
    MatDialogModule,
    MatDividerModule,
    MatTableModule,
    MatSlideToggleModule,
    MatBadgeModule,
    SharedModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TimeoutInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UnauthorizedInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
